import { useId } from "react";

import { ChartState } from "./ChartState";
import s from "./Chart.module.css";
import {
  CHART,
  escalaY,
  etiquetaFecha,
  fechasDe,
  indicesEtiquetas,
  maximoDe,
  seriesConPuntosValidos,
  type ChartProps,
} from "./chart";

export type BarChartProps = ChartProps;

/** Categorías temporales comparadas en barras agrupadas, renderizadas en SVG durante SSR. */
export function BarChart({
  series,
  tz,
  alto = 240,
  vacio = "Sin datos en este rango",
  cargando = false,
  titulo,
  descripcion,
}: BarChartProps) {
  const titleId = useId();
  const descriptionId = useId();
  const seriesValidas = seriesConPuntosValidos(series);
  if (cargando || seriesValidas.length === 0) {
    return <ChartState cargando={cargando} vacio={vacio} titulo={titulo} descripcion={descripcion} />;
  }

  const fechas = fechasDe(seriesValidas);
  const maximo = maximoDe(seriesValidas);
  const anchoUtil = CHART.width - CHART.left - CHART.right;
  const base = alto - CHART.bottom;
  const anchoGrupo = anchoUtil / Math.max(1, fechas.length);
  const anchoBarra = Math.max(2, Math.min(32, (anchoGrupo * 0.72) / seriesValidas.length));

  return (
    <svg
      className={s.chart}
      viewBox={`0 0 ${CHART.width} ${alto}`}
      height={alto}
      role="img"
      aria-labelledby={`${titleId} ${descriptionId}`}
      data-chart="bar"
    >
      <title id={titleId}>{titulo}</title>
      <desc id={descriptionId}>{descripcion}</desc>
      {[0, 0.5, 1].map((proporcion) => {
        const y = CHART.top + proporcion * (base - CHART.top);
        const valor = Math.round(maximo * (1 - proporcion));
        return (
          <g key={proporcion}>
            <line className={s.grid} x1={CHART.left} x2={CHART.width - CHART.right} y1={y} y2={y} />
            <text className={s.axisLabel} x={CHART.left - 8} y={y} textAnchor="end" dominantBaseline="middle">
              {valor}
            </text>
          </g>
        );
      })}
      {indicesEtiquetas(fechas.length).map((indice) => (
        <text key={fechas[indice]} className={s.axisLabel} x={CHART.left + (indice + 0.5) * anchoGrupo} y={alto - 8} textAnchor="middle">
          {etiquetaFecha(fechas[indice], tz)}
        </text>
      ))}
      {seriesValidas.map((serie, serieIndice) => (
        <g key={serie.nombre} className={s.series} data-color={serie.color} data-chart-series={serie.nombre}>
          {fechas.map((fecha, fechaIndice) => {
            const valor = serie.puntos.find((punto) => punto.fecha === fecha)?.valor ?? 0;
            const y = escalaY(valor, maximo, alto);
            const centro = CHART.left + (fechaIndice + 0.5) * anchoGrupo;
            const x = centro - (anchoBarra * seriesValidas.length) / 2 + serieIndice * anchoBarra;
            return (
              <rect
                key={fecha}
                className={s.bar}
                x={x}
                y={y}
                width={Math.max(1, anchoBarra - 2)}
                height={Math.max(0, base - y)}
                rx="2"
                data-chart-bar=""
              />
            );
          })}
        </g>
      ))}
    </svg>
  );
}
